/**
 * BAM IQ's answer, written out at an even pace (Coleman, 2026-11-01: "the text is too choppy as it's produced in the
 * sidebar chat... I wanna make it look nice and smooth"). The model's text arrives in uneven bursts; showing each
 * burst the moment it lands made it jump. Here the text the screen shows catches up with the text that has arrived
 * a little each frame: steady when it's keeping up, faster the further behind it is (a big burst is mostly written
 * out within about half a second). Half-written markdown (an opening ** or a citation tag with no close yet) waits until it's whole, so it
 * never flashes raw symbols.
 *
 * When the stream ends, the saved answer picks up where the stream left off (`handoff`), so the last words keep
 * flowing instead of appearing all at once.
 */
import { useEffect, useRef, useState } from "react";

// Characters a second when keeping up; and the catch-up: each frame closes the gap by frame time / MAX_LAG_S.
export const BASE_CPS = 70;
export const MAX_LAG_S = 0.45;

/** How many characters to show after `dtMs`, given `shown` now and `target` arrived. */
export function revealStep(shown, target, dtMs) {
  if (shown >= target) return target;
  const backlog = target - shown;
  const cps = Math.max(BASE_CPS, backlog / MAX_LAG_S);
  return Math.min(target, shown + Math.max(1, Math.round((cps * dtMs) / 1000)));
}

/** The text up to `n`, cut back before any markdown that isn't finished yet. */
export function safeCut(text, n) {
  let out = String(text || "").slice(0, n);
  if (n >= String(text || "").length) return out;
  // An open citation or card tag ("[S1" without "]").
  const open = out.lastIndexOf("[");
  if (open > out.lastIndexOf("]")) out = out.slice(0, open);
  // An unmatched ** (bold started, not closed).
  const stars = out.split("**").length - 1;
  if (stars % 2 === 1) out = out.slice(0, out.lastIndexOf("**"));
  return out;
}

// Where the stream had got to on screen when it ended, for the saved answer to carry on from.
export const handoff = { len: 0 };

/**
 * `text` revealed smoothly. `from`: how much is already on screen (the saved answer starts where the stream was).
 * `track`: write how far it's got to `handoff`, for the stream.
 */
export function useSmoothText(text, { from = 0, track = false } = {}) {
  const target = String(text || "").length;
  const [shown, setShown] = useState(() => Math.min(from, target));
  const shownRef = useRef(shown);
  // Reduced motion shows it whole; so do the screen tests, which read the text (the pace itself: smoothText.test.js).
  const reduce = process.env.NODE_ENV === "test" || (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches);
  useEffect(() => {
    if (reduce || shownRef.current >= target) { shownRef.current = target; setShown(target); if (track) handoff.len = target; return undefined; }
    let raf = 0, last = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const tick = (now) => {
      const next = revealStep(shownRef.current, target, Math.min(64, now - last));
      last = now;
      shownRef.current = next; setShown(next);
      if (track) handoff.len = next;
      if (next < target) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, reduce, track]);
  return { text: safeCut(text, shown), done: shown >= target };
}
